import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeesApi } from '../../services/employees';

function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: 'unassigned',
    role: 'employee',
    managerId: '',
    status: 'active',
    hireDate: ''
  });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEditing) {
      fetchEmployee();
    }
    fetchEmployees();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const data = await employeesApi.getById(id);
      setFormData({
        name: data.name,
        email: data.email,
        department: data.department,
        role: data.role,
        managerId: data.manager_id || '',
        status: data.status,
        hireDate: data.hire_date ? data.hire_date.split('T')[0] : ''
      });
    } catch (err) {
      setError('Failed to load employee');
      console.error(err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const data = await employeesApi.getAll();
      setEmployees(data.filter(e => e.id !== id));
    } catch (err) {
      console.error('Failed to load employees:', err);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        await employeesApi.update(id, formData);
      } else {
        await employeesApi.create(formData);
      }
      navigate('/employees');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save employee');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>{isEditing ? 'Edit Employee' : 'Add Employee'}</h2>
      </div>

      <div className="card">
        {error && (
          <div style={{ backgroundColor: '#ffebee', color: '#c62828', padding: '12px', borderRadius: '4px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-control"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-control"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isEditing}
            />
          </div>

          <div className="form-group">
            <label htmlFor="hireDate">Joined Date</label>
            <input
              type="date"
              id="hireDate"
              name="hireDate"
              className="form-control"
              value={formData.hireDate}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="department">Department</label>
            <select
              id="department"
              name="department"
              className="form-control"
              value={formData.department}
              onChange={handleChange}
            >
              <option value="unassigned">Unassigned</option>
              <option value="engineering">Engineering</option>
              <option value="sales">Sales</option>
              <option value="hr">HR</option>
              <option value="finance">Finance</option>
              <option value="operations">Operations</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="role">Role</label>
            <select
              id="role"
              name="role"
              className="form-control"
              value={formData.role}
              onChange={handleChange}
            >
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="hr_head">HR Head</option>
              <option value="admin_head">Admin Head</option>
              <option value="accounts_head">Accounts Head</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="managerId">Manager</label>
            <select
              id="managerId"
              name="managerId"
              className="form-control"
              value={formData.managerId}
              onChange={handleChange}
            >
              <option value="">No Manager</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>

          {isEditing && (
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                className="form-control"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="exited">Exited</option>
              </select>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary btn-compact"
              onClick={() => navigate('/employees')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-compact"
              disabled={loading}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmployeeForm;
